import React from 'react';
import { makeStyles, MuiThemeProvider } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import { poslumaTheme, universalStyles } from '../../../../app/theme';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  image: {
    width: '100%',
    height: 'auto',
  },
  heading: {
    paddingTop: 30,
  },
  text: {
    margin: '0 12px 12px 0',
  },
  textBottom: {
    margin: '0 12px 40px 0',
  },
  headerImage: {
    display: 'block',
    width: '100%',
  },
  voidTextStyling: {
    background: 'transparent linear-gradient(270deg, #F0818100 0%, #43C0E8 200%) 0% 0% no-repeat padding-box',
    display: 'flex',
    marginBottom: 15,
  },
  voidText: {
    paddingLeft: 10,
    fontSize: 18,
  },
  superscript: {
    fontSize: 11,
  },
}));

type AcquisitionPage08Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const AcquisitionPage08 = ({ setNextDisabled }: AcquisitionPage08Props): JSX.Element => {
  const classes = useStyles();

  return (
    <MuiThemeProvider theme={poslumaTheme}>
      <div className={classes.poslumaBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[2].ACQUISITION_STEP_4.path}`}
          alt={`${IMAGE_PATHS.STEPS[2].ACQUISITION_STEP_4.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 4:</Typography>
          <Typography variant="h3">Image Acquisition</Typography>
          <Typography className={classes.heading} variant="h4">
            Optimal Timing for Positioning Your Patient for Image Acquisition
          </Typography>
          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.START_IMAGE.path}`}
            alt={`${IMAGE_PATHS.START_IMAGE.alt}`}
          />
          <Typography variant="body1" className={classes.text}>
            Begin image acquisition approximately 60 minutes after POSLUMA injection
            <sup className={classes.superscript}>1</sup>
          </Typography>
          <ul className={classes.poslumaList}>
            <li>
              <span>
                Scan acquisition initiated between 70 and 90 minutes post injection should be performed at the
                discretion of the supervising physician and noted for the interpreting health care professional
                <sup className={classes.superscript}>3,4</sup>
              </span>
            </li>
            <li>
              <span>
                Scan acquisition that begins less than 50 minutes post injection or more than 90 minutes after injection
                may impact diagnostic quality
                <sup className={classes.superscript}>3,4</sup>
              </span>
            </li>
          </ul>
          <Typography variant="h4">How to Position Your Patient During Image Acquisition</Typography>
          <Typography variant="body1" className={classes.text}>
            Position the patient supine with arms above the head
            <sup className={classes.superscript}>1</sup>
          </Typography>
          <ul className={classes.poslumaList}>
            <li>
              <span>
                Patients that cannot raise their arms for the duration of the scan, an alternate position may be used
                <sup className={classes.superscript}>4</sup>
              </span>
            </li>
          </ul>
          <Typography variant="body1" className={classes.text}>
            Image acquisition should start from mid-thigh and proceed to the base of the skull
            <sup className={classes.superscript}>1,5</sup>
          </Typography>
          <Typography variant="body1" className={classes.text}>
            The typical scanning time is 3 minutes per bed position
          </Typography>
          <ul className={classes.poslumaList}>
            <li>
              Scanning should be performed in caudal-to-cranial direction
              <sup className={classes.superscript}>1</sup>
            </li>
          </ul>
          <Typography variant="body1" className={classes.textBottom}>
            Scan duration is approximately 20 minutes depending on the number of bed positions and acquisition time per
            bed position (typically 3 minutes). Adapt imaging technique according to the equipment used and patient
            characteristics in order to obtain the best image quality possible
            <sup className={classes.superscript}>1</sup>
          </Typography>
          <Typography variant="h4">Dosage, Administration, & Image Acquisition Timeline</Typography>
          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.INJECTION_TIMELINE.path}`}
            alt={`${IMAGE_PATHS.INJECTION_TIMELINE.alt}`}
          />
          <ul className={classes.poslumaList}>
            <li>
              <div className={classes.voidTextStyling}>
                <span className={classes.voidText}>
                  <i>
                    <strong>Patients should void immediately prior to imaging</strong>
                    <sup className={classes.superscript}>1</sup>
                  </i>
                </span>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <LearningCheckQuestions module="poslumaAcquisition" page={8} setNextDisabled={setNextDisabled} trainingId={2} />
    </MuiThemeProvider>
  );
};

export default AcquisitionPage08;
